import React, { useState, useEffect } from 'react';
import { Input, Button, Modal, Spin, Select } from 'antd';
import { useDocSetupForm } from './hooks/useDocSetupForm';
import LookupField from '../../components/LookupField';
import apiClient from '../../services/apiClient';

const L102Form: React.FC = () => {
  const f = useDocSetupForm();
  const [searchText, setSearchText] = useState('');
  const [docCategoryOptions, setDocCategoryOptions] = useState<{value: string; label: string}[]>([]);

  useEffect(() => {
    // Load document categories from FL010 lookup
    apiClient.get('/api/l102/lookup/doc-categories').then(res => {
      if (res.data?.data) {
        setDocCategoryOptions(res.data.data.map((item: any) => ({
          value: item.fl010_01 || item.code || '',
          label: `${item.fl010_01 || item.code || ''} ${item.fl010_11 || item.name || ''}`.trim(),
        })));
      }
    }).catch(() => {
      // Fallback: allow free text input if lookup not available
    });
  }, []);

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除', content: `確定要刪除「${f.current.fl020_01}」（${f.current.fl020_11}）？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: f.doDelete,
    });
  };
  const modeLabel = f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={f.loading}>
      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          文件設定 (pwl102f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {f.records.length > 0 ? `${f.currentIdx + 1} / ${f.records.length}` : '無資料'}
          </span>
        </h2>
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="文件編號查詢" value={searchText} onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && f.quickSearch(searchText.trim())}
            disabled={f.isEditing} style={{ width: 200 }} maxLength={10} />
          <Button onClick={() => searchText.trim() && f.quickSearch(searchText.trim())} disabled={f.isEditing}>查詢</Button>
        </div>
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={f.navFirst} disabled={f.isEditing || !f.records.length}>|&lt;</Button>
          <Button size="small" onClick={f.navPrev} disabled={f.isEditing || f.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={f.navNext} disabled={f.isEditing || f.currentIdx >= f.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={f.navLast} disabled={f.isEditing || !f.records.length}>&gt;|</Button>
        </div>
        {/* PK Display */}
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs text-gray-500">文件編號:</span>
          <Input value={f.current?.fl020_01 || (f.mode === 'add' ? '(輸入新編號)' : '')} disabled style={{ width: 150 }} />
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm text-right">文件編號</label>
            <Input value={f.code} onChange={e => f.setCode(e.target.value)} maxLength={10}
              disabled={f.mode !== 'add'} style={{ width: 200 }} />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm text-right">文件名稱</label>
            <Input value={f.docName} onChange={e => f.setDocName(e.target.value)} maxLength={60}
              disabled={!f.isEditing} style={{ width: 400 }} />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm text-right">部門代號</label>
            <LookupField lookupType="department" value={f.deptCode}
              onChange={v => f.setDeptCode(v)} onCodeConfirm={v => f.setDeptCode(v)}
              maxLength={4} disabled={!f.isEditing} style={{ width: 120 }} />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm text-right">文件類別</label>
            <Select
              value={f.docType || undefined}
              onChange={v => f.setDocType(v || '')}
              disabled={!f.isEditing}
              style={{ width: 200 }}
              allowClear
              showSearch
              placeholder="選擇文件類別"
              options={docCategoryOptions.length > 0 ? docCategoryOptions : undefined}
              {...(docCategoryOptions.length === 0 ? { mode: 'tags' as const } : {})}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm text-right">製表人</label>
            <Input value={f.creator} onChange={e => f.setCreator(e.target.value)} maxLength={8}
              disabled={!f.isEditing} style={{ width: 150 }} />
          </div>
        </div>
        {f.current && f.mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6">
            建立: {f.current.fl020_91} | 日期: {f.current.fl020_92 || '-'} | 修改: {f.current.fl020_93}
          </div>
        )}
        <div className="flex items-center gap-2">
          {!f.isEditing ? (
            <>
              <Button type="primary" onClick={f.startAdd}>新增</Button>
              <Button onClick={f.startEdit} disabled={!f.current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!f.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={f.save}>儲存</Button>
              <Button onClick={f.cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default L102Form;
